import { useState } from "react";
import { useWithMessageInput } from "./components/MessageInput";
import { NoteProps } from "./";

type UseWithNoteProps = Pick<
  NoteProps,
  | "createdBy"
  | "adminId"
  | "timestampDate"
  | "note"
  | "noteId"
  | "handleCancelAddNewNote"
  | "handleDeleteNote"
  | "handleEditNote"
  | "handleSaveNewNote"
>;

const useWithNote = ({
  adminId,
  createdBy,
  note,
  noteId,
  timestampDate,
  handleCancelAddNewNote,
  handleDeleteNote,
  handleEditNote,
  handleSaveNewNote,
}: UseWithNoteProps) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [initialInputValue, setInitialInputValue] = useState("");

  const isNewNote = !note;
  const isNoteInputDisabled = !isNewNote && !isEditing;

  const { firstName, lastName, id } = createdBy;

  const isInputVisible = adminId === id && (isEditing || isNewNote);

  const messageInputIdAndName = "MESSAGE_INPUT";

  const handleCancelNote = () => {
    setIsEditing(false);
    setIsSaved(false);
    setInitialInputValue("");
  };

  const messageInputProps = useWithMessageInput({
    id: messageInputIdAndName,
    name: messageInputIdAndName,
    placeHolder: id === adminId ? note : "Type a note",
    initialInputValue: initialInputValue,
    disabled: isNoteInputDisabled,
    isIconVisible: isEditing,
    handleCancelNote,
  });

  messageInputProps.placeHolder = isNewNote
    ? "Type a message"
    : note && messageInputProps.value === "" && !isNoteInputDisabled
    ? "Type a note"
    : messageInputProps.placeHolder;

  const linkText = note ? "Delete this note" : "Cancel";
  const isSaveButtonDisabled =
    isSaved || messageInputProps.value === "" || isSaving;
  const saveButtonText = isSaving ? "Saving..." : isSaved ? "Saved" : "Save";

  const date = new Date(timestampDate);
  const formattedDate = date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  const authorFullName = `Added by ${firstName} ${lastName}:`;

  const handleEditClick = () => {
    setIsEditing(true);
    setIsSaved(false);
    setInitialInputValue(note);
  };

  const handleSaveClick = async () => {
    setIsSaving(true);
    let success = false;
    try {
      const inputNote = messageInputProps.value;
      if (isNewNote) {
        success = await handleSaveNewNote(noteId, inputNote);
      } else {
        success = await handleEditNote(noteId, inputNote);
      }
      setIsSaved(success);
      if (success) {
        setIsEditing(false);
        setInitialInputValue("");
      }
    } catch (error) {
      console.error("Failed to save note:", error);
      setIsSaved(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLinkClick = async () => {
    setIsDeleting(true);

    try {
      isNewNote
        ? await handleCancelAddNewNote(noteId)
        : await handleDeleteNote(noteId);
    } catch (error) {
      console.error("Failed to delete note:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleLinkClickWithPrevention = (
    event: React.MouseEvent<HTMLAnchorElement>
  ) => {
    if (isDeleting) {
      event.preventDefault();
    } else {
      handleLinkClick();
    }
  };

  const saveButton = {
    children: saveButtonText,
    emphasis: "secondary",
    buttonType: "button",
    onClick: handleSaveClick,
    disabled: isSaveButtonDisabled,
  };

  const editButton = {
    children: "edit",
    emphasis: "secondary",
    buttonType: "button",
    onClick: handleEditClick,
    disabled: isEditing,
  };

  const buttons =
    id === adminId
      ? [
          saveButton,
          !isNewNote ? editButton : undefined,
          {
            children: linkText,
            onClick: handleLinkClickWithPrevention,
          },
        ].filter(Boolean)
      : [];

  return {
    authorFullName,
    buttons,
    isDeleting,
    isInputVisible,
    formattedDate,
    formattedTime,
    messageInputProps,
  };
};

export default useWithNote;
